import { memo, forwardRef, useState, useCallback, useMemo, useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';
import { productService } from 'services';
import { Modal, IModal } from 'shared';
import { Column, Button, Row } from 'shared/Houston';
import { enSearchParams } from 'types/enums';
import sortProductsBySelection from 'utils/sortProductsBySelection';

import { IProduct, ISelectedProduct } from '../../types/interfaces/product';
import ProductsList from '../ProductsList';
import ProductsListContext from './ProductsListContext';

interface IProps {
  title: string | React.ReactNode;
  subtitle?: string;
  products: IProduct[];
  multipleSelection?: boolean;
  maxSelectedProducts?: number;
  disabledSelectionProducts?: ISelectedProduct[];
  isInitialized?: boolean;
  selectionProducts?: ISelectedProduct[];
  isEmptyProductList?: boolean;
  isFetchingNextPage: boolean;
  totalProducts?: number;
  isFetchingByTerm: boolean;
  termSearch: enSearchParams;
  closeList: () => void;
  addList: (products: IProduct[]) => void;
  customValidationOnSelect?: (
    productId: number,
    selectedProductsIdsOnList: ISelectedProduct[],
    allProducts: IProduct[]
  ) => boolean;
  onInView: () => void;
}

const ProductsListModal = forwardRef<IModal, IProps>(
  (
    {
      title,
      subtitle,
      products,
      multipleSelection,
      maxSelectedProducts,
      disabledSelectionProducts,
      closeList,
      addList,
      isInitialized = true,
      selectionProducts = [],
      isEmptyProductList: productListEmpty,
      customValidationOnSelect,
      onInView,
      isFetchingNextPage,
      totalProducts,
      isFetchingByTerm,
      termSearch
    },
    ref
  ) => {
    const productsAlreadySelected = useMemo(() => {
      return disabledSelectionProducts?.length ? disabledSelectionProducts : selectionProducts;
    }, [disabledSelectionProducts, selectionProducts]);

    const [, setSearchParams] = useSearchParams();
    const [shouldDisableButton, setShouldDisableButton] = useState(true);
    const [hideInvalidProducts, setHideInvalidProducts] = useState(true);
    const [selectedProducts, setSelectedProducts] = useState(productsAlreadySelected);
    const selectedAtLeastOnce = useRef(productsAlreadySelected);

    const filteredProducts = useMemo(() => {
      if (!hideInvalidProducts) {
        return products;
      }

      return productService.filterValidProducts(products);
    }, [hideInvalidProducts, products]);

    const allProducts = useMemo(() => {
      let allProducts: IProduct[] = [];

      products.forEach(product => {
        allProducts.push({ ...product, children: undefined });

        if (product?.children) {
          allProducts = allProducts.concat(product.children);
        }
      });

      return allProducts;
    }, [products]);

    const handleSelectProduct = useCallback(
      (product: ISelectedProduct) => {
        if (!multipleSelection) {
          setSelectedProducts([product]);
          return;
        }

        const productSelectedIndex = selectedProducts.findIndex(
          selectedProduct => selectedProduct.contentId === product.contentId
        );

        const isProductSelected = productSelectedIndex > -1;

        if (isProductSelected) {
          const currentSelectedProducts = [...selectedProducts];
          const isProductSelectedWithSameOffer = selectedProducts[productSelectedIndex]?.offerId === product?.offerId;

          isProductSelectedWithSameOffer
            ? currentSelectedProducts.splice(productSelectedIndex, 1)
            : (currentSelectedProducts[productSelectedIndex].offerId = product?.offerId);

          setSelectedProducts(currentSelectedProducts);
          return;
        }

        if ((maxSelectedProducts || 1) === selectedProducts.length) {
          return;
        }

        if (customValidationOnSelect && !customValidationOnSelect(product.contentId, selectedProducts, allProducts)) {
          return;
        }

        setSelectedProducts(selectedProduct => [...selectedProduct, product]);
      },
      [selectedProducts, multipleSelection, maxSelectedProducts, customValidationOnSelect, allProducts]
    );

    const handleToggleHideInvalidProducts = useCallback((hideInvalidProducts: boolean) => {
      setHideInvalidProducts(hideInvalidProducts);
    }, []);

    const handleAddProductsList = useCallback(() => {
      const productsAddedWithEmptyKeys = selectedAtLeastOnce.current.filter(product => {
        return selectedProducts.some(selectedProduct => selectedProduct.contentId === product.contentId);
      });

      const addedProducts: IProduct[] = JSON.parse(JSON.stringify(productsAddedWithEmptyKeys));

      selectedProducts.forEach(selectedProduct => {
        const addedProductIndex = addedProducts.findIndex(product => product.contentId === selectedProduct.contentId);

        if (addedProductIndex === -1) {
          return;
        }

        if (selectedProduct?.offerId === addedProducts[addedProductIndex]?.offerId) {
          return;
        }

        addedProducts[addedProductIndex].offerId = selectedProduct?.offerId;
      });

      addList(sortProductsBySelection(selectedProducts, addedProducts));
      setSearchParams(state => {
        state.delete(termSearch);
        return state;
      });
    }, [selectedProducts, addList, setSearchParams, termSearch]);

    const handleCloseModal = useCallback(() => {
      setSearchParams(state => {
        state.delete(termSearch);
        return state;
      });

      setSelectedProducts(productsAlreadySelected);
      closeList();
    }, [setSearchParams, productsAlreadySelected, closeList, termSearch]);

    useEffect(() => {
      const hasSelectedBumps = selectedProducts.length - (disabledSelectionProducts?.length || 0) > 0;
      setShouldDisableButton(!hasSelectedBumps);
    }, [disabledSelectionProducts?.length, selectedProducts.length]);

    useEffect(() => {
      const newProductSelected = allProducts.filter(({ contentId }) => {
        if (selectedAtLeastOnce.current.some(prd => prd.contentId === contentId)) {
          return;
        }

        return selectedProducts.some(prd => prd.contentId === contentId);
      });

      selectedAtLeastOnce.current = [...selectedAtLeastOnce.current, ...newProductSelected];
    }, [allProducts, selectedProducts]);

    return (
      <ProductsListContext.Provider
        value={{
          products: filteredProducts,
          selectedProducts,
          selectionProducts,
          disabledSelectionProducts,
          multipleSelection,
          onToggleHideInvalidProducts: handleToggleHideInvalidProducts,
          onSelectProduct: handleSelectProduct,
          isInitialized,
          isEmptyProductList: productListEmpty || !products.length,
          maxSelectedProducts: maxSelectedProducts,
          isFetchingNextPage,
          onInView,
          totalProducts,
          isFetchingByTerm,
          termSearch
        }}
      >
        <Modal
          id='product-list-modal'
          title={title}
          subtitle={subtitle}
          ref={ref}
          onClickOut={handleCloseModal}
          footer={
            <Row justifyContent='end'>
              <Column>
                <Button variant='text' onClick={handleCloseModal}>
                  Cancelar
                </Button>
              </Column>
              <Column>
                <Button onClick={handleAddProductsList} disabled={shouldDisableButton}>
                  Adicionar
                </Button>
              </Column>
            </Row>
          }
        >
          <ProductsList />
        </Modal>
      </ProductsListContext.Provider>
    );
  }
);

export default memo(ProductsListModal);
